import { useEffect, useMemo, useRef, useState } from 'react';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import { checkSlabFields, type Slab, type SlabKind } from '@obraplan/core/domain/takeoff/slab';
import { retanguloPorDoisCantos } from './fundacao-da-prancha';
import {
  emCentimetros,
  ESPESSURA_PADRAO_EM_CM,
  proximoNomeDeLaje,
  proximoNomeDeRadier,
} from './laje-da-prancha';
import {
  closesPolygon,
  distance,
  metresToPixel,
  pixelToMetres,
  resolvePoint,
  type Pixel,
} from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';
import { ESPESSURA_DA_LAJE_MAXIMA, useConfirmacaoDeValorGrande } from './valor-fora-do-comum';

/**
 * O que as ferramentas "Radier", "Laje" e "Região" fazem na prancha (Fase 27, etapa 3 — D7,
 * RN-369).
 *
 * - **Radier** e **Laje**: o contorno por retângulo de dois cantos — arrastando ou clicando —
 *   ou polígono livre, clicando os vértices e fechando no primeiro, como o bloco. O painel diz
 *   o nome e a espessura em centímetros, e "Adicionar" cadastra;
 * - **Região**: com uma peça selecionada, o mesmo traço — retângulo ou polígono — desenha, por
 *   cima dela, uma região de outra espessura. O domínio recusa a região que sai do contorno e
 *   a que sobrepõe outra, e o painel diz isso na hora, antes de gravar.
 *
 * Com a prancha parada, ou com a ferramenta da peça sem traço pela metade, clicar numa peça a
 * seleciona e arrastá-la a move inteira — as regiões vão junto. O estado mora aqui, e não
 * dentro do traçado, pelo mesmo motivo do hook do bloco: o traçado já carrega o de muitas
 * ferramentas.
 */

export type FerramentaDaLaje = 'RADIER' | 'LAJE' | 'REGIAO';
export type FormatoDaLaje = 'RETANGULO' | 'POLIGONO';

/** O que se pode selecionar na prancha, entre as peças desta fatia. */
export type PecaDaLaje =
  | { readonly tipo: 'LAJE'; readonly id: StructuralPieceId }
  | { readonly tipo: 'REGIAO'; readonly id: StructuralPieceId; readonly indice: number };

/** A peça na mão, arrastada: o pixel por onde ela passa até o botão subir. */
export interface ArrastoDaLaje {
  readonly id: StructuralPieceId;
  readonly origem: Pixel;
  readonly atual: Pixel;
  /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
  readonly moveu: boolean;
}

/** O traço ainda não cadastrado, como a prancha o desenha. */
export interface TracoDaLaje {
  readonly vertices: readonly Pixel[];
  /** Fechado o contorno é polígono cheio; aberto, é uma linha andando até o cursor. */
  readonly fechado: boolean;
  /** Os pontos clicados — as bolinhas. */
  readonly marcados: readonly Pixel[];
  /** O cursor, para o último lado ainda aberto. */
  readonly ao: Pixel | undefined;
}

/** A folga do clique, em pixels **de tela**, como a do encaixe do contorno. */
const FOLGA_DO_ARRASTO = 4;
const FOLGA_VERTICE = 10;
const FOLGA_ORTOGONAL = 8;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

/** Para conferir só o nome e a espessura enquanto o contorno ainda não existe. */
const QUADRADO: readonly Point[] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

const SEM_ALVO = 'Selecione a laje ou o radier antes de desenhar a região.';
const REGIAO_SEM_ESPESSURA = 'A espessura da região deve ser maior que zero.';

function pecaDoAlvo(alvo: Element): StructuralPieceId | undefined {
  const id = alvo.closest('[data-laje]')?.getAttribute('data-laje');
  return id === null || id === undefined ? undefined : (id as StructuralPieceId);
}

export function useLajeNaPrancha({
  draft,
  escala,
  zoom,
  ferramenta,
  parado,
  selecionada,
  dispatch,
  selecionar,
}: {
  draft: TypologyDraft;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta ligada, se for uma destas. Parada, a prancha só seleciona e arrasta. */
  ferramenta: FerramentaDaLaje | undefined;
  /** A prancha está parada — sem ferramenta nenhuma ligada? */
  parado: boolean;
  /** O que está selecionado na prancha, se for uma peça desta fatia: a região nasce nela. */
  selecionada: PecaDaLaje | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  selecionar: (peca: PecaDaLaje) => void;
}) {
  const pecas = useMemo(
    () => (draft.slabs ?? []).filter((peca) => peca.deletedAt === undefined),
    [draft.slabs],
  );
  const radiers = useMemo(() => pecas.filter((peca) => peca.kind === 'RADIER'), [pecas]);
  const lajes = useMemo(() => pecas.filter((peca) => peca.kind === 'LAJE'), [pecas]);

  /** A peça que a região vai receber: a selecionada. */
  const alvo = useMemo(
    (): Slab | undefined =>
      selecionada === undefined ? undefined : pecas.find((peca) => peca.id === selecionada.id),
    [pecas, selecionada],
  );

  const [formato, setFormato] = useState<FormatoDaLaje>('RETANGULO');
  /** Os cantos do retângulo ou os vértices do polígono, em pixel da prancha. */
  const [pontos, setPontos] = useState<readonly Pixel[]>([]);
  const [fechado, setFechado] = useState(false);
  /** O retângulo arrastado, antes de o botão subir. */
  const [gesto, setGesto] = useState<
    { readonly origem: Pixel; readonly atual: Pixel; readonly moveu: boolean } | undefined
  >(undefined);
  const [nomeDigitado, setNomeDigitado] = useState<string | undefined>(undefined);
  /** Centímetros, como a obra fala: 25 de radier, 10 de laje. */
  const [espessuraDoRadier, setEspessuraDoRadier] = useState(ESPESSURA_PADRAO_EM_CM.RADIER);
  const [espessuraDaLaje, setEspessuraDaLaje] = useState(ESPESSURA_PADRAO_EM_CM.LAJE);
  /** Ausente, a região nasce na espessura da peça — o que se muda é o que a faz diferente. */
  const [espessuraDigitadaDaRegiao, setEspessuraDigitadaDaRegiao] = useState<number | undefined>(
    undefined,
  );

  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [arrastando, setArrastando] = useState<ArrastoDaLaje | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  // Trocar de tipologia ou de ferramenta descarta o que estava na mão: o ponto marcado é do
  // desenho e da ferramenta que saíram da mesa.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setPontos([]);
    setFechado(false);
    setGesto(undefined);
    setCursor(undefined);
    setArrastando(undefined);
    setNomeDigitado(undefined);
    setEspessuraDigitadaDaRegiao(undefined);
  }, [emDesenho, ferramenta]);

  // Outra peça escolhida volta a sugerir a espessura dela para a região.
  const idDoAlvo = alvo?.id;
  useEffect(() => {
    setEspessuraDigitadaDaRegiao(undefined);
  }, [idDoAlvo]);

  const kindDaFerramenta: SlabKind = ferramenta === 'RADIER' ? 'RADIER' : 'LAJE';
  const nome =
    nomeDigitado ??
    (ferramenta === 'RADIER' ? proximoNomeDeRadier(radiers) : proximoNomeDeLaje(lajes));
  const espessuraEmCm =
    ferramenta === 'REGIAO'
      ? (espessuraDigitadaDaRegiao ??
        (alvo === undefined ? ESPESSURA_PADRAO_EM_CM.LAJE : emCentimetros(alvo.thickness)))
      : ferramenta === 'RADIER'
        ? espessuraDoRadier
        : espessuraDaLaje;

  /** Os vértices a que o canto novo se encaixa: da peça, se for a região; das peças, se não. */
  const verticesParaEncaixe = useMemo((): readonly Pixel[] => {
    if (escala === undefined) return [];
    const base =
      ferramenta === 'REGIAO'
        ? alvo === undefined
          ? []
          : [alvo.outline, ...alvo.regions.map((regiao) => regiao.outline)]
        : pecas.map((peca) => peca.outline);
    return base.flatMap((contornoDaPeca) =>
      contornoDaPeca.map((ponto) => metresToPixel(ponto, escala)),
    );
  }, [escala, ferramenta, alvo, pecas]);

  /** O contorno de agora, em metros — ausente enquanto o traço não fechou. */
  const contorno = useMemo((): readonly Point[] | undefined => {
    if (escala === undefined) return undefined;
    if (formato === 'RETANGULO') {
      const [a, b] = pontos;
      return a === undefined || b === undefined
        ? undefined
        : retanguloPorDoisCantos(pixelToMetres(a, escala), pixelToMetres(b, escala));
    }
    return fechado ? pontos.map((ponto) => pixelToMetres(ponto, escala)) : undefined;
  }, [escala, formato, pontos, fechado]);

  /**
   * O que impede cadastrar o traço de agora — o painel diz, e o botão espera.
   *
   * É a mesma conferência do domínio, antes de gravar: a região fora do contorno ou sobre
   * outra aparece aqui, enquanto o traço ainda pode ser refeito.
   */
  const impedimento = useMemo((): string | undefined => {
    const espessura = espessuraEmCm / 100;
    if (ferramenta === 'REGIAO') {
      if (alvo === undefined) return SEM_ALVO;
      // Sem contorno ainda, não há o que conferir além da espessura.
      if (contorno === undefined) {
        return Number.isFinite(espessura) && espessura > 0 ? undefined : REGIAO_SEM_ESPESSURA;
      }
      const conferida = checkSlabFields(
        alvo.kind,
        {
          name: alvo.name,
          outline: alvo.outline,
          thickness: alvo.thickness,
          regions: [...alvo.regions, { outline: contorno, thickness: espessura }],
        },
        pecas,
        alvo.id,
      );
      return conferida.ok ? undefined : conferida.error.message;
    }
    const conferida = checkSlabFields(
      kindDaFerramenta,
      { name: nome, outline: contorno ?? QUADRADO, thickness: espessura, regions: [] },
      pecas,
    );
    return conferida.ok ? undefined : conferida.error.message;
  }, [ferramenta, alvo, contorno, espessuraEmCm, nome, kindDaFerramenta, pecas]);

  /** Há traço pela metade? Aí o clique é do traço, e a peça que está embaixo deixa passar. */
  const emTraco = pontos.length > 0 || gesto !== undefined;

  /**
   * Cada tipo de peça responde a clique com a prancha parada, ou com a ferramenta dele sem
   * traço — a laje por baixo do radier, por exemplo, deixa o clique do radier passar. A
   * região nova nasce sobre a peça: aí nada é clicável, porque o clique é um canto.
   */
  const clicaveis: Readonly<Record<SlabKind, boolean>> = {
    RADIER: parado || (ferramenta === 'RADIER' && !emTraco),
    LAJE: parado || (ferramenta === 'LAJE' && !emTraco),
  };

  /** Onde o canto cai: encaixado no vértice de uma peça, ou onde foi clicado. */
  function cantoNovo(bruto: Pixel, anterior?: Pixel): Pixel {
    return resolvePoint(bruto, {
      vertices: [...verticesParaEncaixe, ...pontos],
      vertexTolerance: FOLGA_VERTICE / zoom,
      ...(anterior === undefined
        ? {}
        : { previous: anterior, orthogonalTolerance: FOLGA_ORTOGONAL / zoom }),
    });
  }

  /** O clique no vazio da prancha, com uma das ferramentas ligadas. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (escala === undefined || ferramenta === undefined) return;

    if (formato === 'RETANGULO') {
      const canto = cantoNovo(bruto);
      const primeiro = pontos[0];
      if (pontos.length === 1 && primeiro !== undefined) {
        // O segundo clique no mesmo canto não é retângulo de tamanho zero.
        if (distance(primeiro, canto) < FOLGA_VERTICE / zoom) return;
        setPontos([primeiro, canto]);
        return;
      }
      // Zero ou dois cantos: o clique começa outro retângulo.
      setPontos([canto]);
      return;
    }
    if (fechado) {
      setFechado(false);
      setPontos([cantoNovo(bruto)]);
      return;
    }
    if (closesPolygon(bruto, pontos, FOLGA_VERTICE / zoom)) {
      setFechado(true);
      return;
    }
    setPontos((atual) => [...atual, cantoNovo(bruto, atual[atual.length - 1])]);
  }

  /** O botão desceu na prancha: com o retângulo ligado, pode ser o começo de um arrasto. */
  function apertarNaPrancha(ponto: Pixel): void {
    if (ferramenta === undefined || formato !== 'RETANGULO' || escala === undefined) return;
    setGesto({ origem: ponto, atual: ponto, moveu: false });
  }

  /** O botão desceu numa peça: se andar, ela vai junto, com as regiões dela. */
  function apertarNaPeca(alvoDoClique: Element, ponto: Pixel): boolean {
    if (escala === undefined) return false;
    const id = pecaDoAlvo(alvoDoClique);
    const peca = id === undefined ? undefined : pecas.find((candidata) => candidata.id === id);
    if (id === undefined || peca === undefined || !clicaveis[peca.kind]) return false;
    setArrastando({ id, origem: ponto, atual: ponto, moveu: false });
    return true;
  }

  /** O mouse andou: a peça na mão, o retângulo arrastado e o segmento vivo. */
  function acompanhar(ponto: Pixel): boolean {
    if (arrastando !== undefined) {
      setArrastando({
        ...arrastando,
        atual: ponto,
        moveu: arrastando.moveu || distance(arrastando.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    if (gesto !== undefined) {
      setGesto({
        ...gesto,
        atual: ponto,
        moveu: gesto.moveu || distance(gesto.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    // O segmento vivo só existe com traço pela metade; fora disso o cursor não custa render.
    const aberto =
      ferramenta !== undefined &&
      ((formato === 'RETANGULO' && pontos.length === 1) ||
        (formato === 'POLIGONO' && pontos.length > 0 && !fechado));
    if (aberto) setCursor(ponto);
    return false;
  }

  /** O botão subiu: grava o movimento, ou fecha o retângulo arrastado. */
  function soltar(ponto: Pixel): boolean {
    const corrente = arrastando;
    if (corrente !== undefined) {
      setArrastando(undefined);
      if (escala === undefined) return true;
      // Soltar onde apertou é clique, e clicar seleciona: a peça não sai do lugar.
      if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return true;
      }
      cliqueDoArrasto.current = Date.now();

      const peca = pecas.find((candidata) => candidata.id === corrente.id);
      if (peca === undefined) return true;
      const dx = (ponto.x - corrente.origem.x) * escala;
      const dy = (ponto.y - corrente.origem.y) * escala;
      // O contorno é o mesmo, só deslocado: é o que diz à bancada que as regiões vão junto.
      dispatch({
        type: 'UPDATE_SLAB',
        pieceId: corrente.id,
        outline: peca.outline.map((vertice) => ({ x: vertice.x + dx, y: vertice.y + dy })),
      });
      return true;
    }

    const desenhado = gesto;
    if (desenhado !== undefined) {
      setGesto(undefined);
      // Sem andar, é clique: o clique que vem a seguir marca o canto.
      if (!desenhado.moveu && distance(desenhado.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return false;
      }
      cliqueDoArrasto.current = Date.now();
      const a = cantoNovo(desenhado.origem);
      const b = cantoNovo(ponto);
      if (distance(a, b) >= FOLGA_VERTICE / zoom) setPontos([a, b]);
      return true;
    }
    return false;
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez.
   *
   * O navegador o entrega no mesmo instante em que o botão sobe; a janela existe para que um
   * arrasto cujo clique nunca veio — soltar fora da prancha — não engula o clique seguinte,
   * que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  function limparTraco(): void {
    setPontos([]);
    setFechado(false);
    setGesto(undefined);
    setCursor(undefined);
  }

  /** A laje de 3 m de espessura pede conferência: o aviso aparece e o cadastro vale no segundo clique. */
  const confirmaEspessura = useConfirmacaoDeValorGrande(
    espessuraEmCm / 100,
    ESPESSURA_DA_LAJE_MAXIMA,
  );

  function adicionar(): void {
    if (contorno === undefined || impedimento !== undefined || ferramenta === undefined) return;
    if (!confirmaEspessura.confirmar()) return;
    confirmaEspessura.limpar();
    if (ferramenta === 'REGIAO') {
      if (alvo === undefined) return;
      dispatch({
        type: 'ADD_SLAB_REGION',
        pieceId: alvo.id,
        outline: contorno,
        thickness: espessuraEmCm / 100,
      });
    } else {
      dispatch({
        type: 'ADD_SLAB',
        kind: kindDaFerramenta,
        name: nome,
        outline: contorno,
        thickness: espessuraEmCm / 100,
      });
      setNomeDigitado(undefined);
    }
    limparTraco();
  }

  /** O traço como o desenho o mostra: o retângulo, ou o polígono andando. */
  const traco = ((): TracoDaLaje | undefined => {
    if (ferramenta === undefined) return undefined;
    if (formato === 'RETANGULO') {
      const [a, b] = pontos;
      if (gesto?.moveu === true) {
        return {
          vertices: retanguloPorDoisCantos(gesto.origem, gesto.atual),
          fechado: true,
          marcados: [gesto.origem],
          ao: undefined,
        };
      }
      if (a !== undefined && b !== undefined) {
        return {
          vertices: retanguloPorDoisCantos(a, b),
          fechado: true,
          marcados: [a, b],
          ao: undefined,
        };
      }
      if (a !== undefined) {
        return {
          vertices: cursor === undefined ? [] : retanguloPorDoisCantos(a, cursor),
          fechado: true,
          marcados: [a],
          ao: undefined,
        };
      }
      return undefined;
    }
    if (pontos.length === 0) return undefined;
    return { vertices: pontos, fechado, marcados: pontos, ao: fechado ? undefined : cursor };
  })();

  return {
    ferramenta,
    pecas,
    radiers,
    lajes,
    alvo,
    formato,
    escolherFormato: (novo: FormatoDaLaje) => {
      setFormato(novo);
      limparTraco();
    },
    cantos: pontos.length,
    fechado,
    contorno,
    traco,
    nome,
    digitarNome: setNomeDigitado,
    espessuraEmCm,
    mudarEspessuraEmCm: (valor: number) => {
      if (ferramenta === 'REGIAO') setEspessuraDigitadaDaRegiao(valor);
      else if (ferramenta === 'RADIER') setEspessuraDoRadier(valor);
      else setEspessuraDaLaje(valor);
    },
    impedimento,
    avisoDaEspessura: confirmaEspessura.aviso,
    adicionar,
    fecharContorno: () => {
      if (formato === 'POLIGONO' && pontos.length >= 3) setFechado(true);
    },
    desfazerPonto: () => {
      if (fechado) setFechado(false);
      else setPontos((atual) => atual.slice(0, -1));
    },
    refazer: limparTraco,
    // O que os eventos do desenho chamam
    arrastando,
    clicaveis,
    apertarNaPrancha,
    apertarNaPeca,
    acompanhar,
    soltar,
    cancelarArrasto: () => {
      setArrastando(undefined);
      setGesto(undefined);
    },
    foiCliqueDoArrasto,
    clicarNaPrancha,
    clicarNaLaje: (id: StructuralPieceId) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'LAJE', id });
    },
    clicarNaRegiao: (id: StructuralPieceId, indice: number) => {
      if (foiCliqueDoArrasto()) return;
      selecionar({ tipo: 'REGIAO', id, indice });
    },
  };
}

export type LajeNaPrancha = ReturnType<typeof useLajeNaPrancha>;
